import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import {
  BiBookHeart,
  BiBookOpen,
  BiStar,
  BiBookAdd,
  BiPurchaseTagAlt,
  BiGift,
} from 'react-icons/bi';

import { PrimaryButton } from 'account-dashboard/styled';
import { colors, fonts } from 'account-dashboard/tokens';
import Chevron from 'account-dashboard/chevron';
import { bookFormat, pageCount, enabledFeatures, accessPassMonths, discountLines } from './details';

// "Your book details" — shown immediately after a successful redemption, with the
// celebration animation. Every tile is driven by the redeemed journal's own
// entitlement summary; nothing here is hardcoded from the design.
const Screen = styled.div`
  background: #f1ecfc;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  max-width: 358px;
  margin: 0 auto;
  padding: 40px 0 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const Title = styled.h1`
  font-family: ${fonts.heading};
  font-weight: 500;
  font-size: 30px;
  line-height: 1.2;
  letter-spacing: -0.18px;
  color: ${colors.text};
  text-align: center;
  margin: 0;
`;

const Tiles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const Tile = styled.div`
  background: #fff;
  border: 1px solid ${colors.chipBorder};
  border-radius: 16px;
  padding: 10px 16px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
`;

const TileIcon = styled.span`
  display: flex;
  align-items: center;
  padding: 4px 0;
  flex-shrink: 0;
  color: ${colors.text};
`;

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const TileTitle = styled.p`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 18px;
  line-height: 1.5;
  letter-spacing: -0.108px;
  color: #000;
  margin: 0;
`;

const TileBullet = styled.p`
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  color: #000;
  margin: 0;
`;

const TileNote = styled.p`
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: -0.32px;
  color: ${colors.muted};
  margin: 0;
`;

const MoreDetails = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: -0.096px;
  color: #000;
  margin: 0;

  strong {
    font-weight: 600;
  }
`;

const Action = styled.div`
  width: 100%;
  padding-top: 10px;
`;

const YourBookDetails = ({ entitlements, onStart }) => {
  const { width, height } = useWindowSize();

  const format = bookFormat(entitlements);
  const pages = pageCount(entitlements);
  const features = enabledFeatures(entitlements);
  const months = accessPassMonths(entitlements);
  const discounts = discountLines(entitlements);

  return (
    <Screen>
      {/* Same celebration treatment the app already uses elsewhere. `recycle`
          is off so it plays once on entering this screen rather than looping. */}
      <Confetti
        width={width}
        height={height}
        numberOfPieces={2000}
        recycle={false}
        style={{ pointerEvents: 'none' }}
      />

      <Content>
        <Title>Your book details</Title>

        <Tiles>
          {!!format && (
            <Tile>
              <TileIcon>
                <BiBookHeart size={20} />
              </TileIcon>
              <TileBody>
                <TileTitle>{format}</TileTitle>
              </TileBody>
            </Tile>
          )}

          {!!pages && (
            <Tile>
              <TileIcon>
                <BiBookOpen size={20} />
              </TileIcon>
              <TileBody>
                <TileTitle>{`${pages} pages`}</TileTitle>
              </TileBody>
            </Tile>
          )}

          {!!features.length && (
            <Tile>
              <TileIcon>
                <BiStar size={20} />
              </TileIcon>
              <TileBody>
                <TileTitle>Special baby pages</TileTitle>
                {features.map(feature => (
                  <TileBullet key={feature}>{` • ${feature}`}</TileBullet>
                ))}
              </TileBody>
            </Tile>
          )}

          {!!months && (
            <Tile>
              <TileIcon>
                <BiBookAdd size={20} />
              </TileIcon>
              <TileBody>
                <TileTitle>{`${months} months to add stories`}</TileTitle>
                <TileNote>
                  From the baby&rsquo;s birth (sign up date if started later)
                </TileNote>
              </TileBody>
            </Tile>
          )}

          {!!discounts.length && (
            <Tile>
              <TileIcon>
                <BiPurchaseTagAlt size={20} />
              </TileIcon>
              <TileBody>
                <TileTitle>Future discounts</TileTitle>
                {discounts.map(line => (
                  <TileBullet key={line}>{` • ${line}`}</TileBullet>
                ))}
              </TileBody>
            </Tile>
          )}
        </Tiles>

        <MoreDetails>
          <BiGift size={20} />
          <span>
            +More gift details in <strong>My Account</strong>
          </span>
        </MoreDetails>

        <Action>
          <PrimaryButton type="button" onClick={onStart}>
            Start my book
            <Chevron />
          </PrimaryButton>
        </Action>
      </Content>
    </Screen>
  );
};

YourBookDetails.propTypes = {
  // The redeemed journal's entitlement summary object.
  entitlements: PropTypes.object,
  onStart: PropTypes.func.isRequired,
};

YourBookDetails.defaultProps = {
  entitlements: {},
};

export default YourBookDetails;
